/**
 * Navigation / Dead-Reckoning utilities
 *
 * Pure helpers plus the per-day state-machine resolver for the Stormwrack
 * "lost at sea" navigation model. Mirrors the clock-utils split: primitives
 * here, orchestration in the component.
 *
 * Geometry is optional: absolute hex coords only move when navigation.grid has
 * been seeded (by the OBR extension). Without it, the resolver still runs the
 * full state machine and records direction/distance in the log.
 */

import { v4 as uuidv4 } from 'uuid';
import {
  NavigationState,
  NavLogEntry,
  HexCoord,
  HexDirection,
  NavGrid
} from '../types/navigation-types';

/** Axial neighbor offsets for a pointy-top hex, indexed by HexDirection. */
const HEX_NEIGHBORS: Record<HexDirection, HexCoord> = {
  0: { q: 1, r: 0 },   // E
  1: { q: 1, r: -1 },  // NE
  2: { q: 0, r: -1 },  // NW
  3: { q: -1, r: 0 },  // W
  4: { q: -1, r: 1 },  // SW
  5: { q: 0, r: 1 }    // SE
};

/** Default compass label per direction for a pointy-top grid (no grid rotation). */
const DEFAULT_COMPASS: Record<HexDirection, string> = {
  0: 'E', 1: 'NE', 2: 'NW', 3: 'W', 4: 'SW', 5: 'SE'
};

const COMPASS_8 = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/** The neighboring hex in a given direction. */
export function hexNeighbor(hex: HexCoord, dir: HexDirection): HexCoord {
  const d = HEX_NEIGHBORS[dir];
  return { q: hex.q + d.q, r: hex.r + d.r };
}

/** Axial (cube) distance in hexes between two coordinates. */
export function hexDistance(a: HexCoord, b: HexCoord): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}

/** Walk `count` hexes from `hex` along a single direction. */
export function hexStep(hex: HexCoord, dir: HexDirection, count: number): HexCoord {
  let out = hex;
  for (let i = 0; i < count; i++) out = hexNeighbor(out, dir);
  return out;
}

/** Random hex-face direction (d6, zero-indexed). */
export function rollDrift(): HexDirection {
  return Math.floor(Math.random() * 6) as HexDirection;
}

/** Stormwrack recognition DC: 20, -1 per day of random travel (floored at 1). */
export function recognitionDC(daysOfRandomTravel: number): number {
  return Math.max(1, 20 - Math.floor(daysOfRandomTravel));
}

/** Human compass label for a direction, rotated by the grid if one is seeded. */
export function directionToCompass(dir: HexDirection, grid?: NavGrid): string {
  if (!grid) return DEFAULT_COMPASS[dir];
  const bearing = ((grid.direction0Bearing + dir * 60) % 360 + 360) % 360;
  const idx = Math.round(bearing / 45) % 8;
  return COMPASS_8[idx];
}

/** Build a log entry, filling id + timestamp. */
export function createNavLogEntry(
  entry: Omit<NavLogEntry, 'id' | 'timestamp'>
): NavLogEntry {
  return { ...entry, id: uuidv4(), timestamp: Date.now() };
}

export interface ResolveNavInput {
  nav: NavigationState;
  /** Pilot's total Knowledge (Geography) result today. */
  roll: number;
  /** DC used while on course (the day's navigation DC for the conditions). */
  onCourseDC: number;
  /** DC used while lost-aware (course-set DC with unknown-start modifier). */
  rechartDC: number;
  /** Consecutive failed navigation checks *before* today. */
  navConsecutiveFailures: number;
  /** The voyage day being resolved. */
  day: number;
  /** Pilot character name for log reasons. */
  pilotName: string;
}

export interface ResolveNavOutput {
  nav: NavigationState;
  logs: NavLogEntry[];
  navConsecutiveFailures: number;
  /** DM-facing summary of what happened. */
  summary: string;
}

/**
 * Resolve one day of the voyage from a single Pilot roll. The roll's meaning
 * depends on status: navigation (on-course), recognition (lost-unaware), or
 * re-chart (lost-aware). Returns the new navigation state, log entries to
 * append, the updated consecutive-failure count, and a DM summary.
 *
 * WHO MOVES WHAT. The crew sails the believed token by hand on the map between
 * days — it is the ship as far as anyone aboard knows, and it may cover any
 * distance, or none, or jump outright. This function never moves it (except to
 * reunite it with the truth on a successful re-chart). The truth token is the
 * derived one: while the ship is on course it simply follows the believed token,
 * and once it is off course it goes its own way.
 *
 * THE DAY'S RUN. How far the crew sailed the believed token since the last
 * resolved day is how far the ship actually travelled, so that is how far it
 * drifts when off course — a day of sailing covers the same water whether or not
 * it is the water they intended. A rolled d6 supplies the direction. The run is
 * floored at one hex: a ship at sea makes way even when nobody moved the token,
 * and while lost-aware the believed token is hidden and *cannot* move.
 */
export function resolveDay(input: ResolveNavInput): ResolveNavOutput {
  const { nav, roll, onCourseDC, rechartDC, navConsecutiveFailures, day, pilotName } = input;
  const grid = nav.grid;
  const logs: NavLogEntry[] = [];

  // Shallow clone so we never mutate the Firebase-sourced state.
  const next: NavigationState = {
    ...nav,
    believed: { ...nav.believed },
    truth: { ...nav.truth },
    updatedBy: 'dashboard',
    rev: (nav.rev ?? 0) + 1
  };
  let consec = navConsecutiveFailures;
  let summary = '';

  const dayRun = Math.max(
    1,
    nav.dayStartBelieved ? hexDistance(nav.dayStartBelieved, nav.believed.hex) : 0
  );

  const drift = (): void => {
    const dir = rollDrift();
    const compass = directionToCompass(dir, grid);
    next.daysOfRandomTravel = next.daysOfRandomTravel + 1;
    if (grid) next.truth.hex = hexStep(next.truth.hex, dir, dayRun);
    logs.push(createNavLogEntry({
      periodNumber: day,
      eventType: 'drift',
      direction: dir,
      compassLabel: compass,
      distanceHexes: dayRun,
      truthHex: grid ? next.truth.hex : undefined,
      daysOfRandomTravel: next.daysOfRandomTravel,
      reason: `Off course: the ship makes ${dayRun} hex${dayRun === 1 ? '' : 'es'} ${compass}.`,
      isDMOnly: true
    }));
  };

  switch (nav.status) {
    case 'on-course': {
      const success = roll >= onCourseDC;
      if (success) {
        consec = 0;
        // Still on course, so the truth is wherever they sailed to.
        next.truth.hex = next.believed.hex;
        logs.push(createNavLogEntry({
          periodNumber: day,
          eventType: 'advance',
          believedHex: grid ? next.believed.hex : undefined,
          truthHex: grid ? next.truth.hex : undefined,
          check: { skill: 'Knowledge (Geography)', dc: onCourseDC, roll, success: true },
          reason: `${pilotName} holds the course (nav check ${roll} vs DC ${onCourseDC}).`,
          isDMOnly: false
        }));
        summary = 'On course. The ship holds true.';
      } else {
        consec = navConsecutiveFailures + 1;
        if (consec >= 2) {
          next.status = 'lost-unaware';
          next.lostSincePeriod = day;
          logs.push(createNavLogEntry({
            periodNumber: day,
            eventType: 'became-lost',
            check: { skill: 'Knowledge (Geography)', dc: onCourseDC, roll, success: false },
            reason: `${pilotName} fails a second straight navigation check — the ship has drifted off course, but the crew does not yet know it.`,
            isDMOnly: true
          }));
          // The ship still sailed today, just not where they think. Drifting now
          // rather than next day is what pulls the two tokens apart in the first
          // place — leave it out and the day they get lost costs them nothing.
          drift();
          summary = 'DM ONLY: the ship is now lost — the crew is unaware.';
        } else {
          next.truth.hex = next.believed.hex;
          logs.push(createNavLogEntry({
            periodNumber: day,
            eventType: 'advance',
            check: { skill: 'Knowledge (Geography)', dc: onCourseDC, roll, success: false },
            reason: `${pilotName} misses the navigation check (${roll} vs DC ${onCourseDC}). One more failure and the ship is off course.`,
            isDMOnly: false
          }));
          summary = 'Navigation check failed. One more consecutive failure and the ship is lost.';
        }
      }
      break;
    }

    case 'lost-unaware': {
      // The believed token has already been sailed on down the crew's imagined
      // course by hand. Only the truth moves here, and it moves elsewhere.
      drift();
      const recDC = recognitionDC(next.daysOfRandomTravel);
      const recognized = roll >= recDC;
      if (recognized) {
        next.status = 'lost-aware';
        next.believed.display = 'hidden';
        next.recognizedPeriod = day;
        logs.push(createNavLogEntry({
          periodNumber: day,
          eventType: 'recognized',
          check: { skill: 'Knowledge (Geography)', dc: recDC, roll, success: true },
          daysOfRandomTravel: next.daysOfRandomTravel,
          reason: `${pilotName} realizes the ship is lost (recognition ${roll} vs DC ${recDC}).`,
          isDMOnly: false
        }));
        summary = 'The crew realizes they are lost. Believed position hidden until they re-chart.';
      } else {
        logs.push(createNavLogEntry({
          periodNumber: day,
          eventType: 'recognition-check',
          check: { skill: 'Knowledge (Geography)', dc: recDC, roll, success: false },
          daysOfRandomTravel: next.daysOfRandomTravel,
          reason: `${pilotName} still believes the ship is on course (secretly failed recognition ${roll} vs DC ${recDC}).`,
          isDMOnly: true
        }));
        summary = 'DM ONLY: crew remains unaware they are lost. The map keeps lying to them.';
      }
      break;
    }

    case 'lost-aware': {
      drift();
      const success = roll >= rechartDC;
      if (success) {
        next.status = 'on-course';
        next.believed.display = 'normal';
        next.believed.hex = next.truth.hex;
        next.daysOfRandomTravel = 0;
        consec = 0;
        logs.push(createNavLogEntry({
          periodNumber: day,
          eventType: 'rechart',
          believedHex: grid ? next.believed.hex : undefined,
          check: { skill: 'Knowledge (Geography)', dc: rechartDC, roll, success: true },
          reason: `${pilotName} re-establishes the ship's position and sets a new course (${roll} vs DC ${rechartDC}).`,
          isDMOnly: false
        }));
        summary = 'Position re-established. Back on course.';
      } else {
        logs.push(createNavLogEntry({
          periodNumber: day,
          eventType: 'rechart',
          check: { skill: 'Knowledge (Geography)', dc: rechartDC, roll, success: false },
          reason: `${pilotName} cannot re-establish the ship's position (${roll} vs DC ${rechartDC}). The ship drifts on.`,
          isDMOnly: false
        }));
        summary = 'Re-chart failed. The ship is still lost and drifting.';
      }
      break;
    }

    default:
      summary = 'Navigation is not active (chart a course first).';
      // No state change; drop the speculative rev bump.
      next.rev = nav.rev ?? 0;
      return { nav, logs: [], navConsecutiveFailures, summary };
  }

  // Today's finishing position is tomorrow's starting line, so the next day's
  // run measures only what the crew sails from here.
  next.dayStartBelieved = next.believed.hex;

  return { nav: next, logs, navConsecutiveFailures: consec, summary };
}

/**
 * Record the initial chart-course result at voyage start. Success -> on-course;
 * failure (or storm) -> lost-unaware from the outset.
 */
export function chartCourse(
  nav: NavigationState,
  opts: { success: boolean; period: number; pilotName: string; destinationHex?: HexCoord; heading?: HexDirection }
): { nav: NavigationState; logs: NavLogEntry[] } {
  const { success, period, pilotName, destinationHex, heading } = opts;
  const next: NavigationState = {
    ...nav,
    believed: { ...nav.believed },
    truth: { ...nav.truth },
    heading: heading ?? nav.heading,
    destinationHex: destinationHex ?? nav.destinationHex,
    status: success ? 'on-course' : 'lost-unaware',
    lostSincePeriod: success ? undefined : period,
    daysOfRandomTravel: 0,
    // The starting line for day one's run: whatever the crew sails from here.
    dayStartBelieved: nav.believed.hex,
    updatedBy: 'dashboard',
    rev: (nav.rev ?? 0) + 1
  };

  const logs: NavLogEntry[] = [createNavLogEntry({
    periodNumber: period,
    eventType: 'chart-course',
    reason: success
      ? `${pilotName} plots a course to the destination.`
      : `${pilotName} plots a course, but navigation errors mean the ship sets out already off track.`,
    isDMOnly: false
  })];

  if (!success) {
    logs.push(createNavLogEntry({
      periodNumber: period,
      eventType: 'became-lost',
      reason: 'The plotted course is wrong — the ship is off track from the start, and the crew is unaware.',
      isDMOnly: true
    }));
  }

  return { nav: next, logs };
}

/** DM-triggered landfall: believed snaps to truth and everyone can see it again. */
export function arrive(
  nav: NavigationState,
  opts: { period: number; destinationName?: string }
): { nav: NavigationState; logs: NavLogEntry[] } {
  const next: NavigationState = {
    ...nav,
    believed: { ...nav.truth, display: 'normal' },
    truth: { ...nav.truth },
    status: 'arrived',
    arrivedPeriod: opts.period,
    updatedBy: 'dashboard',
    rev: (nav.rev ?? 0) + 1
  };
  const logs = [createNavLogEntry({
    periodNumber: opts.period,
    eventType: 'arrived',
    believedHex: nav.grid ? next.believed.hex : undefined,
    truthHex: nav.grid ? next.truth.hex : undefined,
    reason: opts.destinationName
      ? `Landfall: the ship reaches ${opts.destinationName}. Position is known again.`
      : 'Landfall: the ship reaches a recognizable location. Position is known again.',
    isDMOnly: false
  })];
  return { nav: next, logs };
}
